<template>
  <SlideOverTray :open="!!itemId" :width-class="isStaffView ? 'w-full max-w-2xl' : 'w-full max-w-xl'" @close="$emit('close')">
    <div class="flex flex-col h-full overflow-hidden">
        <!-- Loading skeleton: only while there is nothing at all to show -->
        <div v-if="loading && !item" class="flex flex-col animate-pulse">
          <div class="px-5 py-4 border-b border-gray-200">
            <div class="flex items-center gap-2 mb-2">
              <div class="h-5 w-16 bg-gray-200 rounded"></div>
              <div class="h-5 w-20 bg-gray-200 rounded"></div>
            </div>
            <div class="h-6 w-3/4 bg-gray-200 rounded"></div>
          </div>
          <div class="px-5 py-5 space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div v-for="n in 4" :key="n">
                <div class="h-3 w-16 bg-gray-100 rounded mb-1.5"></div>
                <div class="h-4 w-24 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div>
              <div class="h-3 w-20 bg-gray-100 rounded mb-1.5"></div>
              <div class="h-20 bg-gray-100 rounded"></div>
            </div>
          </div>
        </div>

        <template v-else-if="item">
          <!-- Header -->
          <div class="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white shrink-0">
            <!-- Breadcrumb (staff only: parent linkage is planning context) -->
            <div v-if="isStaffView && breadcrumb.length > 1" class="flex items-center gap-1 text-xs text-gray-400 mb-2 flex-wrap">
              <template v-for="(crumb, i) in breadcrumb" :key="crumb.id">
                <button
                  v-if="i < breadcrumb.length - 1"
                  class="hover:text-primary-500 truncate max-w-[160px] sensitive"
                  data-demo-blur
                  @click="$emit('select-item', crumb.id)"
                >
                  {{ crumb.title }}
                </button>
                <span v-else class="text-gray-600 font-medium truncate max-w-[160px] sensitive" data-demo-blur>{{ crumb.title }}</span>
                <FontAwesomeIcon v-if="i < breadcrumb.length - 1" :icon="['fas', 'chevron-right']" class="text-[8px] text-gray-300" />
              </template>
            </div>
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2 flex-wrap">
                <Support_InlineDropdown
                  v-if="isStaffView"
                  :model-value="item.requirement_type"
                  :options="typeOptions"
                  :chip-class="typeClass(item.requirement_type_name)"
                  :display-label="item.requirement_type_name"
                  @update:model-value="setType"
                />
                <span
                  v-else
                  class="text-[11px] font-medium px-1.5 py-0.5 rounded"
                  :class="typeClass(item.requirement_type_name)"
                >
                  {{ typeLabel(item.requirement_type_name) }}
                </span>
                <span
                  class="text-xs px-2 py-0.5 rounded"
                  :class="statusClass(item.status)"
                  :title="statusTitle(item.status)"
                  role="img"
                  :aria-label="statusAriaLabel(item.status)"
                >
                  {{ statusLabel(item.status) }}
                </span>
                <SourceBadge v-if="item.source" :source="item.source" />
                <span
                  v-if="isStaffView && isAwaitingStaffReply"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700"
                  :title="customerRepliedTitle"
                >Customer replied</span>
                <button
                  type="button"
                  class="text-[11px] font-mono transition-colors"
                  :class="idCopied ? 'text-green-600' : 'text-gray-400 hover:text-primary-600'"
                  :title="idCopied ? 'Copied!' : 'Copy ticket number'"
                  :aria-label="'Copy ticket number ' + item.id"
                  @click="copyTicketId"
                >#{{ item.id }}</button>
              </div>
              <button
                class="text-gray-400 hover:text-gray-600 p-1"
                @click="$emit('close')"
              >
                <FontAwesomeIcon :icon="['fas', 'times']" />
              </button>
            </div>

            <!-- Title: click the pencil to edit (staff) -->
            <div class="flex items-start gap-2">
              <template v-if="editingTitle">
                <input
                  ref="titleInput"
                  v-model="titleDraft"
                  class="flex-1 text-lg font-semibold text-gray-900 border border-primary-300 rounded px-2 py-1 outline-none ring-1 ring-primary-100"
                  @keydown.enter.prevent="saveTitle"
                  @keydown.escape.stop="cancelTitle"
                  @blur="saveTitle"
                />
              </template>
              <template v-else>
                <h2 class="text-lg font-semibold text-gray-900 break-words sensitive" data-demo-blur>{{ item.title }}</h2>
                <button
                  v-if="isStaffView"
                  type="button"
                  class="shrink-0 mt-1.5 text-gray-300 hover:text-primary-500"
                  title="Edit title"
                  aria-label="Edit title"
                  @click="startEditTitle"
                >
                  <FontAwesomeIcon :icon="['fas', 'pencil']" class="text-xs" />
                </button>
              </template>
              <span
                v-if="fieldSaveState.title"
                class="text-[11px] font-normal mt-2 shrink-0"
                :class="fieldSaveState.title === 'saved' ? 'text-emerald-600' : 'text-gray-400'"
              >
                {{ saveText(fieldSaveState.title) }}
              </span>
            </div>

            <a
              :href="ticketMailtoHref"
              class="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              <FontAwesomeIcon :icon="['fas', 'envelope']" class="text-[10px]" />
              Email about this ticket
            </a>
            <div v-if="isStaffView && item.reply_address" class="flex items-center gap-1.5 mt-2 text-[11px]">
              <FontAwesomeIcon :icon="['fas', 'at']" class="text-[10px] text-gray-400 shrink-0" />
              <span class="text-gray-500">Forward customer emails here:</span>
              <code class="font-mono text-primary-600 truncate">{{ item.reply_address }}</code>
              <button
                type="button"
                class="shrink-0 font-medium transition-colors"
                :class="replyAddressCopied ? 'text-green-600' : 'text-gray-400 hover:text-primary-600'"
                :title="replyAddressCopied ? 'Copied!' : 'Copy forward address'"
                :aria-label="'Copy forward address for ticket #' + item.id"
                @click="copyReplyAddress"
              >
                <FontAwesomeIcon :icon="['fas', replyAddressCopied ? 'check' : 'copy']" class="mr-0.5" />
                {{ replyAddressCopied ? 'Copied' : 'Copy' }}
              </button>
            </div>

            <!-- Customer visibility: a toggle for staff, with the legend -->
            <div v-if="isStaffView" class="flex items-center gap-2 mt-2 flex-wrap">
              <button
                type="button"
                class="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded transition-colors"
                :class="item.customer_facing !== false
                  ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'"
                :title="item.customer_facing !== false ? 'Click to make this internal only' : 'Click to make this visible to the customer'"
                @click="toggleCustomerFacing"
              >
                <FontAwesomeIcon :icon="['fas', item.customer_facing !== false ? 'eye' : 'lock']" class="text-[9px]" />
                {{ item.customer_facing !== false ? 'Customer can see this' : 'Internal only — the customer can\'t see this' }}
                <span v-if="fieldSaveState.customer_facing" class="ml-1 font-normal text-gray-400">{{ saveText(fieldSaveState.customer_facing) }}</span>
              </button>
              <div class="relative">
                <button
                  class="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
                  @click.stop="toggleCustomerLegend"
                >
                  <FontAwesomeIcon :icon="['fas', 'circle-info']" class="text-[9px]" />
                  {{ isInternalOnly ? 'What this means' : 'What the customer sees' }}
                </button>
                <div
                  v-if="showCustomerLegend"
                  class="absolute z-20 top-full left-0 mt-1.5 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left"
                  @click.stop
                >
                  <template v-if="isInternalOnly">
                    <p class="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                      <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[10px]" />
                      The customer sees none of this
                    </p>
                    <p class="text-[11px] text-gray-600">
                      This ticket is marked internal, so it doesn't appear in the
                      customer's Service Desk at all — not the title, the
                      description, or the status. Only your team can see it.
                    </p>
                  </template>
                  <template v-else>
                    <p class="text-[11px] font-semibold text-emerald-700 flex items-center gap-1.5 mb-1.5">
                      <FontAwesomeIcon :icon="['fas', 'eye']" class="text-[10px]" />
                      Visible to the customer
                    </p>
                    <ul class="text-[11px] text-gray-600 space-y-0.5 mb-3 pl-4 list-disc">
                      <li>Title and description</li>
                      <li>Due date, requester, created/updated</li>
                      <li>
                        Status, in customer-friendly wording — <strong>Waiting</strong>
                        reads as "Needs Input" and asks them to reply
                      </li>
                      <li>Replies to their comments</li>
                    </ul>
                    <p class="text-[11px] font-semibold text-amber-700 flex items-center gap-1.5 mb-1.5">
                      <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[10px]" />
                      Never shown to the customer
                    </p>
                    <ul class="text-[11px] text-gray-600 space-y-0.5 pl-4 list-disc">
                      <li>Internal notes</li>
                      <li>Owner, priority and due-date changes</li>
                      <li>The Activity log (staff-only, enforced by the API)</li>
                      <li>Comments marked as internal notes</li>
                    </ul>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Scrollable content -->
          <div class="flex-1 overflow-y-auto">
            <div class="px-5 py-5 space-y-5">
              <!-- Triage panel -->
              <TriagePanel
                v-if="item.needs_triage && isStaffView"
                :requirement-id="item.id"
                :sender-email="firstEmailSender"
                @triaged="onTriaged"
              />

              <!-- Meta fields -->
              <div class="grid grid-cols-2 gap-x-6 gap-y-2.5">
                <div v-if="item.requester_name || isStaffView">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Requester</label>
                  <div class="text-sm text-gray-900 sensitive">{{ item.requester_name || 'Unknown' }}</div>
                </div>
                <div v-if="isStaffView">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Tenant
                    <span v-if="fieldSaveState.tenant" class="ml-1 font-normal" :class="badgeClass(fieldSaveState.tenant)">{{ saveText(fieldSaveState.tenant) }}</span>
                  </label>
                  <select
                    :value="item.tenant ?? ''"
                    class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-300"
                    @change="setTenant($event.target.value)"
                  >
                    <option v-if="!item.tenant" value="">No tenant</option>
                    <option v-for="t in tenantOptions" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                </div>
                <div v-if="isStaffView">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Owner
                    <span v-if="fieldSaveState.owner" class="ml-1 font-normal" :class="badgeClass(fieldSaveState.owner)">
                      <FontAwesomeIcon :icon="['fas', saveIcon(fieldSaveState.owner)]" :class="{ 'animate-spin': fieldSaveState.owner === 'saving' }" />
                      {{ saveText(fieldSaveState.owner) }}
                    </span>
                  </label>
                  <Support_SearchableSelect
                    :model-value="item.owner ?? null"
                    :options="ownerOptions"
                    placeholder="Unassigned"
                    none-placeholder="Unassigned"
                    @update:model-value="setOwner"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Priority
                    <span v-if="fieldSaveState.priority" class="ml-1 font-normal" :class="badgeClass(fieldSaveState.priority)">
                      <FontAwesomeIcon :icon="['fas', saveIcon(fieldSaveState.priority)]" :class="{ 'animate-spin': fieldSaveState.priority === 'saving' }" />
                      {{ saveText(fieldSaveState.priority) }}
                    </span>
                  </label>
                  <Support_PriorityRating
                    v-if="isStaffView"
                    :model-value="priorityValue"
                    :clearable="true"
                    @update:model-value="setPriority"
                  />
                  <div v-else class="text-sm">
                    <span v-if="priorityLabel" class="font-medium" :class="priorityColorClass">{{ priorityLabel }}</span>
                    <span v-else class="text-gray-400">Not set</span>
                  </div>
                </div>
                <div v-if="isStaffView">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Status
                    <span v-if="fieldSaveState.status" class="ml-1 font-normal" :class="badgeClass(fieldSaveState.status)">
                      <FontAwesomeIcon :icon="['fas', saveIcon(fieldSaveState.status)]" :class="{ 'animate-spin': fieldSaveState.status === 'saving' }" />
                      {{ saveText(fieldSaveState.status) }}
                    </span>
                  </label>
                  <select
                    :value="item.status"
                    class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-300"
                    @change="setStatus($event.target.value)"
                  >
                    <option v-for="s in statusOptions" :key="s.value" :value="s.value">
                      {{ s.label }}
                    </option>
                  </select>
                  <p
                    v-if="item.status === 'waiting'"
                    class="mt-1 text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1 flex items-start gap-1.5"
                  >
                    <FontAwesomeIcon :icon="['fas', 'circle-exclamation']" class="text-[10px] mt-0.5 shrink-0" />
                    <span>
                      The customer sees <strong>Needs Input</strong> and is asked to
                      reply. Use <strong>In Progress</strong> if you're the one
                      working on it.
                    </span>
                  </p>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Due Date
                    <span v-if="fieldSaveState.due_date" class="ml-1 font-normal" :class="badgeClass(fieldSaveState.due_date)">
                      <FontAwesomeIcon :icon="['fas', saveIcon(fieldSaveState.due_date)]" :class="{ 'animate-spin': fieldSaveState.due_date === 'saving' }" />
                      {{ saveText(fieldSaveState.due_date) }}
                    </span>
                  </label>
                  <input
                    v-if="isStaffView"
                    type="date"
                    :value="dueDateInputValue"
                    class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-300"
                    :class="dueDateClass(item.due_date)"
                    @change="setDueDate($event.target.value)"
                  />
                  <div v-else class="text-sm" :class="dueDateClass(item.due_date)">
                    {{ formatDate(item.due_date) || 'Not set' }}
                  </div>
                </div>
                <div v-if="isStaffView && canChangeParent">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Parent
                    <button
                      v-if="item.parent"
                      class="ml-1 font-normal text-primary-500 hover:text-primary-700"
                      data-testid="parent-link"
                      :title="parentItem ? parentItem.title : `Open #${item.parent}`"
                      @click="$emit('select-item', item.parent)"
                    >
                      Open #{{ item.parent }}
                      <FontAwesomeIcon :icon="['fas', 'arrow-up-right-from-square']" class="text-[8px]" />
                    </button>
                    <span v-if="fieldSaveState.parent" class="ml-1 font-normal" :class="badgeClass(fieldSaveState.parent)">{{ saveText(fieldSaveState.parent) }}</span>
                  </label>
                  <Support_SearchableSelect
                    :model-value="item.parent ?? null"
                    :options="parentOptions"
                    :exclude-id="item.id"
                    placeholder="None (top-level)"
                    @update:model-value="setParent"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Created</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.created_at) }}</div>
                </div>
                <div v-if="item.updated_at">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Updated</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.updated_at) }}</div>
                </div>
                <div v-if="isStaffView && item.first_response_at">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">First response</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.first_response_at) }}</div>
                </div>
                <div v-if="isStaffView && item.resolution_at">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Resolved</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.resolution_at) }}</div>
                </div>
              </div>

              <!-- Ownership bar (customer view) -->
              <div
                v-if="isCustomerView && ownership"
                role="status"
                class="rounded-lg border px-4 py-3"
                :class="ownership.theirBall
                  ? 'border-orange-200 bg-orange-50'
                  : 'border-gray-200 bg-white'"
              >
                <div class="flex items-start gap-2.5">
                  <FontAwesomeIcon
                    :icon="['fas', ownership.theirBall ? 'circle-exclamation' : 'gear']"
                    class="text-[13px] mt-0.5 shrink-0"
                    :class="ownership.theirBall ? 'text-orange-600' : 'text-gray-400'"
                  />
                  <div class="min-w-0">
                    <div
                      class="text-sm font-semibold"
                      :class="ownership.theirBall ? 'text-orange-900' : 'text-gray-700'"
                    >
                      {{ ownership.theirBall ? 'Waiting on you' : 'With the Kairon team' }}
                      <span v-if="ownership.sinceLabel" class="font-normal text-orange-700">
                        · {{ ownership.sinceLabel }}
                      </span>
                    </div>
                    <p v-if="ownership.theirBall" class="text-[12px] mt-0.5 text-orange-800">
                      Our team has a question for you — check the latest comment
                      below. We'll keep going as soon as you reply.
                    </p>
                    <p v-else class="text-[12px] mt-0.5 text-gray-500">
                      Our team is working on this. Nothing is needed from you —
                      we'll update you here as it moves.
                    </p>
                    <div v-if="ownership.theirBall" class="flex flex-wrap items-center gap-2 mt-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded px-2.5 py-1 transition-colors"
                        @click="focusCommentComposer"
                      >
                        <FontAwesomeIcon :icon="['fas', 'comment']" class="text-[10px]" />
                        Reply here
                      </button>
                      <a
                        :href="ticketMailtoHref"
                        class="inline-flex items-center gap-1.5 text-xs font-medium text-orange-800 hover:text-orange-900 underline underline-offset-2"
                      >
                        <FontAwesomeIcon :icon="['fas', 'envelope']" class="text-[10px]" />
                        Reply by email instead
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="isCustomerView && customerStatusBucket === 'resolved'"
                role="status"
                class="rounded-lg border border-green-200 bg-green-50 px-4 py-3"
              >
                <div class="flex items-start gap-2.5">
                  <FontAwesomeIcon
                    :icon="['fas', 'check-circle']"
                    class="text-[13px] mt-0.5 shrink-0 text-green-600"
                  />
                  <div class="min-w-0">
                    <div class="text-sm font-semibold text-green-900">
                      Resolved on {{ formatDate(item.completed_at || item.updated_at) }}
                    </div>
                    <p class="text-[12px] mt-0.5 text-green-800">
                      If something's still not right, reply below. Our team will review it and reopen it if needed.
                    </p>
                    <div class="mt-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded px-2.5 py-1 transition-colors"
                        @click="focusCommentComposer"
                      >
                        <FontAwesomeIcon :icon="['fas', 'comment']" class="text-[10px]" />
                        Reply
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Customer lifecycle stepper -->
              <div v-if="isCustomerView" class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div class="flex items-center">
                  <template v-for="(step, i) in customerSteps" :key="step.key">
                    <div class="flex flex-col items-center text-center">
                      <span
                        class="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold"
                        :class="step.attention ? 'bg-orange-500 text-white' : step.done ? 'bg-green-500 text-white' : step.current ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-400'"
                        :title="step.hint"
                        role="img"
                        :aria-label="`Step ${i + 1} of ${customerSteps.length}: ${step.label} — ${step.hint}`"
                      >
                        <FontAwesomeIcon v-if="step.attention" :icon="['fas', 'circle-exclamation']" class="text-[11px]" />
                        <FontAwesomeIcon v-else-if="step.done" :icon="['fas', 'check']" class="text-[10px]" />
                        <span v-else>{{ i + 1 }}</span>
                      </span>
                      <span
                        class="text-[11px] font-medium mt-1"
                        :class="step.attention ? 'text-orange-800' : step.current || step.done ? 'text-gray-800' : 'text-gray-400'"
                      >{{ step.label }}</span>
                      <span v-if="step.at" class="text-[10px] text-gray-400">{{ formatDate(step.at) }}</span>
                    </div>
                    <div
                      v-if="i < customerSteps.length - 1"
                      class="flex-1 h-0.5 mx-2"
                      :class="customerSteps[i + 1].done || customerSteps[i + 1].current ? 'bg-green-400' : 'bg-gray-200'"
                    />
                  </template>
                </div>
                <p v-if="customerStatusBucket !== 'resolved'" class="text-[11px] text-gray-500 mt-3 flex items-center gap-1.5">
                  <FontAwesomeIcon :icon="['fas', 'clock']" class="text-[10px]" />
                  {{ needsCustomerInput
                    ? "We'll pick this straight back up once you reply."
                    : 'We typically respond within 1 business day.' }}
                </p>
              </div>

              <!-- Description: editable for staff, read-only for customers -->
              <div v-if="isStaffView || item.description">
                <label class="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  Description
                  <span v-if="isStaffView && item.customer_facing !== false" class="inline-flex items-center gap-1 text-emerald-600 font-normal">
                    <FontAwesomeIcon :icon="['fas', 'eye']" class="text-[10px]" />
                    Visible to the customer
                  </span>
                  <span v-if="fieldSaveState.description" class="ml-auto font-normal" :class="badgeClass(fieldSaveState.description)">
                    {{ saveText(fieldSaveState.description) }}
                  </span>
                </label>
                <textarea
                  v-if="isStaffView"
                  ref="descriptionTextarea"
                  v-model="descriptionDraft"
                  rows="2"
                  placeholder="Describe the work..."
                  class="w-full text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none overflow-y-auto max-h-64 sensitive"
                  data-demo-blur
                  @input="autoResize($event.target)"
                  @blur="saveDescription"
                ></textarea>
                <div v-else class="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg px-4 py-3 sensitive" data-demo-blur>{{ item.description }}</div>
              </div>

              <!-- Tags (staff only) -->
              <div v-if="isStaffView">
                <label class="block text-xs font-medium text-gray-500 mb-1">Tags</label>
                <Support_TagEditor
                  :model-value="item.tags || []"
                  @update:model-value="setTags"
                />
              </div>

              <!-- Internal notes (staff only) -->
              <div v-if="isStaffView">
                <label class="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[10px] text-amber-600" />
                  Internal notes
                  <span class="text-amber-600 font-normal">Only your team can see this — never shown to customers</span>
                  <span v-if="fieldSaveState.internal_notes" class="font-normal ml-auto" :class="badgeClass(fieldSaveState.internal_notes)">
                    {{ saveText(fieldSaveState.internal_notes) }}
                  </span>
                </label>
                <textarea
                  ref="internalNotesTextarea"
                  v-model="internalNotesDraft"
                  rows="2"
                  placeholder="Add an internal note..."
                  class="w-full text-sm text-gray-800 whitespace-pre-wrap bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none overflow-y-auto max-h-48 sensitive"
                  data-demo-blur
                  @input="autoResize($event.target)"
                  @blur="saveInternalNotes"
                ></textarea>
              </div>

              <!-- Children (staff, parent types only) -->
              <div v-if="isStaffView && canAddChild">
                <div class="flex items-center justify-between mb-1.5">
                  <label class="block text-xs font-medium text-gray-500">
                    Children{{ childItems.length ? ` (${childItems.length})` : '' }}
                  </label>
                  <button
                    v-if="!addingChild"
                    class="text-[10px] text-gray-400 hover:text-primary-500 flex items-center gap-0.5"
                    @click="startAddChild"
                  >
                    <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[8px]" />
                    Add
                  </button>
                </div>
                <div class="space-y-1">
                  <button
                    v-for="child in childItems"
                    :key="child.id"
                    class="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-gray-50 group transition-colors"
                    @click="$emit('select-item', child.id)"
                  >
                    <span class="text-[10px] font-medium px-1 py-0.5 rounded shrink-0" :class="typeClass(child.requirement_type_name)">
                      {{ child.requirement_type_name }}
                    </span>
                    <span class="text-sm text-gray-700 truncate flex-1 sensitive" data-demo-blur>{{ child.title }}</span>
                    <span class="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0" :class="statusClass(child.status)">
                      {{ STATUS_LABELS[child.status] || child.status }}
                    </span>
                  </button>
                  <div v-if="addingChild" class="flex items-center gap-2 px-2.5 py-1.5">
                    <select
                      v-model="newChildTypeId"
                      class="text-[10px] font-medium border border-gray-200 rounded px-1 py-0.5 bg-white"
                    >
                      <option v-for="t in allowedChildTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
                    </select>
                    <input
                      ref="childTitleInput"
                      v-model="newChildTitle"
                      class="flex-1 text-sm border border-gray-200 rounded px-2 py-1 outline-none focus:border-primary-300"
                      placeholder="Title..."
                      @keydown.enter="submitChild"
                      @keydown.escape.stop="addingChild = false"
                    />
                    <button
                      class="text-xs text-primary-500 hover:text-primary-700 disabled:opacity-40"
                      :disabled="!newChildTitle.trim()"
                      @click="submitChild"
                    >
                      <FontAwesomeIcon :icon="['fas', 'check']" />
                    </button>
                    <button class="text-xs text-gray-400 hover:text-gray-600" @click="addingChild = false">
                      <FontAwesomeIcon :icon="['fas', 'xmark']" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Attachments -->
              <div v-if="item.id">
                <label class="block text-xs font-medium text-gray-500 mb-1">Attachments</label>
                <RequirementAttachmentList
                  v-if="requirementMediaFiles.length"
                  :files="requirementMediaFiles"
                  :use-media-endpoint="true"
                  :removable="canDeleteAttachments"
                  class="mb-2"
                  @remove="removeAttachment"
                />
                <RequirementAttachmentList
                  v-else-if="legacyAttachments.length"
                  :files="legacyAttachments"
                  class="mb-2"
                />
                <RequirementAttachmentUpload
                  ref="attachUpload"
                  :requirement-id="item.id"
                  :surface="true"
                  :drop-label="`to ticket #${item.id}`"
                  @uploaded="onAttachmentUploaded"
                />
              </div>

              <!-- Comment Thread -->
              <div class="border-t border-gray-200 pt-5">
                <label class="text-sm font-medium text-gray-700 mb-3 block">Comments</label>
                <RequirementCommentThread
                  v-if="item.id"
                  ref="commentThread"
                  :key="item.id"
                  :requirement-id="item.id"
                  :force-customer-view="isCustomerView"
                  :customer-facing="item.customer_facing !== false"
                  @comment-count="onNewCommentCount"
                  @new-comments="onNewComments"
                  @comment-posted="onCommentPosted"
                />
              </div>

              <!-- Activity (staff only) -->
              <div v-if="!isCustomerView" class="border-t border-gray-200 pt-5">
                <label class="text-sm font-medium text-gray-700 mb-2.5 block">Activity</label>
                <div v-if="activities.length" class="space-y-2.5">
                  <div v-for="a in activities" :key="a.id" class="flex gap-2 text-sm">
                    <div class="mt-0.5" :class="a.activity_type === 'commented' ? 'text-primary-500' : 'text-gray-400'">
                      <FontAwesomeIcon :icon="activityIcon(a.activity_type)" class="w-3.5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <span class="text-gray-700" v-html="formatActivity(a)"></span>
                      <div
                        v-if="activityCommentPreview(a)"
                        class="mt-1 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5 whitespace-pre-wrap line-clamp-3 sensitive"
                        data-demo-blur
                      >{{ activityCommentPreview(a) }}</div>
                      <div class="text-xs text-gray-400 mt-0.5">{{ relativeTime(a.created_at) }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-400">{{ activitiesError ? 'Could not load activity.' : 'No activity yet' }}</div>
              </div>

              <!-- Delete (staff only) -->
              <div v-if="isStaffView" class="border-t border-gray-200 pt-4">
                <button
                  v-if="!showDeleteConfirm"
                  class="text-sm text-red-500 hover:text-red-700"
                  @click="showDeleteConfirm = true"
                >
                  Delete
                </button>
                <div v-else-if="!childItems.length" class="flex items-center gap-2">
                  <span class="text-sm text-gray-600">Delete this item?</span>
                  <button class="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700" @click="deleteWithChildren">Delete</button>
                  <button class="text-xs text-gray-400 hover:text-gray-600" @click="showDeleteConfirm = false">Cancel</button>
                </div>
                <div v-else class="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                  <div class="text-xs text-red-700">
                    Has {{ descendantCount }} {{ descendantCount === 1 ? 'descendant' : 'descendants' }}
                  </div>
                  <div class="flex flex-col gap-1">
                    <button class="text-xs text-left px-2 py-1.5 rounded border border-red-200 text-gray-700 hover:bg-red-100" @click="deleteAndPromoteChildren">
                      <FontAwesomeIcon :icon="['fas', 'arrow-up']" class="mr-1 text-gray-400" />
                      Move children up, then delete
                    </button>
                    <button class="text-xs text-left px-2 py-1.5 rounded bg-red-600 text-white hover:bg-red-700" @click="deleteWithChildren">
                      <FontAwesomeIcon :icon="['fas', 'trash']" class="mr-1" />
                      Delete everything
                    </button>
                    <button class="text-xs text-center text-gray-400 hover:text-gray-600 py-0.5" @click="showDeleteConfirm = false">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="px-5 py-12 text-center text-sm text-gray-400">
          This item could not be loaded.
        </div>
      </div>
  </SlideOverTray>
</template>

<script>
import { createTicket, deleteAttachment, deleteTicket, getTicketHistory, listAttachments, listComments, listTickets } from '@/services/api/serviceDesk';
import { mapGetters } from 'vuex';
import { useToast } from 'vue-toastification';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SlideOverTray from './SlideOverTray.vue';
import SourceBadge from './SourceBadge.vue';
import TriagePanel from './TriagePanel.vue';
import RequirementCommentThread from './RequirementCommentThread.vue';
import RequirementAttachmentList from './RequirementAttachmentList.vue';
import RequirementAttachmentUpload from './RequirementAttachmentUpload.vue';
import Support_InlineDropdown from './Support_InlineDropdown.vue';
import Support_PriorityRating from './Support_PriorityRating.vue';
import Support_SearchableSelect from './Support_SearchableSelect.vue';
import Support_TagEditor from './Support_TagEditor.vue';
import { isDemoMode } from '@/utils/demoSpoof';
import undoQueue from './supportUndo';
import {
  CHILD_TYPES_FOR_PARENT,
  PRIORITY_INT_TO_CODE,
  PRIORITY_LABELS,
  SELECTABLE_STATUSES,
  STATUS_CLASSES,
  STATUS_LABELS,
  TYPE_CLASSES,
  VALID_PARENT_TYPES,
  WORK_TYPES,
  awaitingStaffReply,
  canParent,
  customerBucket,
  customerRepliedLabel,
  customerStatusClass,
  customerStatusHint,
  customerStatusLabel,
  customerTypeLabel,
  daysAgoLabel,
  daysUntil,
  formatDateValue,
  getDescendantIds,
  priorityInt,
} from './support_constants';

// Guards the breadcrumb walk against a corrupt or very deep parent chain.
const MAX_ANCESTOR_DEPTH = 10;

const FIELD_LABELS = {
  due_date: 'due date',
  priority: 'priority',
  status: 'status',
  owner: 'owner',
  title: 'title',
  description: 'description',
  internal_notes: 'internal notes',
  tenant: 'tenant',
  parent: 'parent',
  requirement_type: 'type',
  customer_facing: 'visibility',
  tags: 'tags',
};

export default {
  name: 'Support_ItemDetail',
  components: {
    FontAwesomeIcon,
    SlideOverTray,
    SourceBadge,
    TriagePanel,
    RequirementCommentThread,
    RequirementAttachmentList,
    RequirementAttachmentUpload,
    Support_InlineDropdown,
    Support_PriorityRating,
    Support_SearchableSelect,
    Support_TagEditor,
  },
  props: {
    itemId: { type: [String, Number], default: null },
    forceCustomerView: { type: Boolean, default: false },
    users: { type: Array, default: () => [] },
    requirementTypes: { type: Array, default: () => [] },
    tenantOptions: { type: Array, default: () => [] },
    allItems: { type: Array, default: () => [] },
    // Which list this drawer was opened from; a newly created child joins it.
    scope: { type: String, default: 'work' },
  },
  emits: ['close', 'comment-count', 'triaged', 'select-item', 'deleted'],
  data() {
    return {
      STATUS_LABELS,
      loading: false,
      loadFailed: false,
      activities: [],
      activitiesError: false,
      requirementMediaFiles: [],
      firstEmailSenderValue: null,
      showCustomerLegend: false,
      idCopied: false,
      replyAddressCopied: false,
      fieldSaveState: {},
      editingTitle: false,
      titleDraft: '',
      descriptionDraft: '',
      internalNotesDraft: '',
      addingChild: false,
      newChildTitle: '',
      newChildTypeId: null,
      showDeleteConfirm: false,
      // Hierarchy fetched for this item on its own, so a parent or child the
      // opening list filtered out (done rows, another tenant) still shows.
      fetchedChildIds: [],
      fetchedAncestorIds: [],
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    toast() {
      return useToast();
    },
    itemIdNumber() {
      return this.itemId != null && this.itemId !== '' ? Number(this.itemId) : null;
    },
    // The row lives in the store; the drawer is a view onto it.
    item() {
      if (this.itemIdNumber == null) return null;
      return this.$store.getters['supportItems/item'](this.itemIdNumber);
    },
    isStaff() {
      return this.current_user?.is_staff || this.current_user?.is_superuser;
    },
    isCustomerView() {
      return !this.isStaff || this.forceCustomerView;
    },
    isStaffView() {
      return !this.isCustomerView;
    },
    isInternalOnly() {
      return this.item?.customer_facing === false;
    },
    canDeleteAttachments() {
      return (
        this.current_user?.is_superuser ||
        this.$store.getters['tenant/isTenantAdmin'] ||
        this.$store.getters['tenant/isSecurityUser']
      );
    },
    legacyAttachments() {
      return this.item?.metadata?.attachments || [];
    },
    firstEmailSender() {
      return this.firstEmailSenderValue;
    },
    priorityValue() {
      return priorityInt(this.item?.priority);
    },
    priorityLabel() {
      if (this.priorityValue == null) return null;
      return PRIORITY_LABELS[PRIORITY_INT_TO_CODE[this.priorityValue]] || `P${this.priorityValue}`;
    },
    priorityColorClass() {
      const map = { 0: 'text-red-700', 1: 'text-orange-700', 2: 'text-amber-700', 3: 'text-gray-600' };
      return map[this.priorityValue] || 'text-gray-600';
    },
    statusOptions() {
      const values = [...SELECTABLE_STATUSES];
      const current = this.item?.status;
      if (current && !values.includes(current)) values.push(current);
      return values.map((value) => ({ value, label: STATUS_LABELS[value] || value }));
    },
    typeOptions() {
      return this.requirementTypes
        .filter((t) => WORK_TYPES.has(t.name))
        .map((t) => ({ value: t.id, label: t.name, chipClass: TYPE_CLASSES[t.name] || 'bg-gray-100 text-gray-600' }));
    },
    ownerOptions() {
      const options = this.users.map((u) => ({ id: u.id, title: u.full_name || u.email }));
      if (this.item?.owner && !options.some((o) => o.id === this.item.owner)) {
        options.push({ id: this.item.owner, title: this.item.owner_name || `User ${this.item.owner}` });
      }
      return options;
    },
    dueDateInputValue() {
      return this.item?.due_date ? String(this.item.due_date).slice(0, 10) : '';
    },
    ticketMailtoHref() {
      const subject = `Ticket #${this.item?.id} — ${this.item?.title || ''}`.trim();
      return `mailto:support@kaironhealth.com?subject=${encodeURIComponent(subject)}`;
    },
    customerStatusBucket() {
      return customerBucket(this.item?.status);
    },
    isAwaitingStaffReply() {
      return awaitingStaffReply(this.item);
    },
    customerRepliedTitle() {
      return customerRepliedLabel(this.item);
    },
    needsCustomerInput() {
      return this.customerStatusBucket === 'waiting';
    },
    ownership() {
      const bucket = this.customerStatusBucket;
      if (bucket !== 'in_progress' && bucket !== 'waiting') return null;
      const theirBall = bucket === 'waiting';
      let sinceLabel = '';
      if (theirBall && this.item?.waiting_since) {
        const elapsed = daysAgoLabel(this.item.waiting_since);
        const date = formatDateValue(this.item.waiting_since, { month: 'short', day: 'numeric' });
        if (date) sinceLabel = elapsed ? `since ${date} · ${elapsed}` : `since ${date}`;
      }
      return { theirBall, sinceLabel };
    },
    customerSteps() {
      const order = ['received', 'in_progress', 'resolved'];
      const bucket = this.customerStatusBucket;
      const needsInput = this.needsCustomerInput;
      const stage = needsInput ? 'in_progress' : bucket;
      const currentIndex = Math.max(order.indexOf(stage), 0);
      const lifecycle = this.item?.lifecycle || {};
      const at = {
        received: lifecycle.received_at || this.item?.created_at,
        in_progress: lifecycle.in_progress_at,
        resolved: lifecycle.resolved_at || this.item?.completed_at,
      };
      const labels = {
        received: 'Received',
        in_progress: needsInput ? 'Needs Input' : 'In Progress',
        resolved: 'Resolved',
      };
      return order.map((key, i) => {
        const done = i < currentIndex;
        const current = i === currentIndex;
        const attention = needsInput && key === 'in_progress';
        let hint;
        if (attention) hint = 'Waiting on you — we need information before we can continue';
        else if (done) hint = 'Done';
        else if (current) hint = customerStatusHint(key);
        else hint = 'Not yet';
        return { key, label: labels[key], at: at[key] || null, done, current, attention, hint };
      });
    },
    // Hierarchy helpers read the list the drawer was opened from.
    itemsById() {
      const map = {};
      const storeRows = this.$store.getters['supportItems/byId'];
      for (const id of this.fetchedAncestorIds) if (storeRows[id]) map[id] = storeRows[id];
      for (const row of this.allItems) map[row.id] = row;
      return map;
    },
    breadcrumb() {
      const chain = [];
      let current = this.item;
      const seen = new Set();
      while (current && !seen.has(current.id)) {
        seen.add(current.id);
        chain.unshift(current);
        current = current.parent ? this.itemsById[current.parent] : null;
      }
      return chain;
    },
    parentItem() {
      return this.item?.parent ? this.itemsById[this.item.parent] || null : null;
    },
    childItems() {
      if (!this.item) return [];
      const children = this.allItems.filter((row) => row.parent === this.item.id);
      const seen = new Set(children.map((row) => row.id));
      const storeRows = this.$store.getters['supportItems/byId'];
      for (const id of this.fetchedChildIds) {
        const row = storeRows[id];
        if (row && row.parent === this.item.id && !seen.has(id)) {
          children.push(row);
          seen.add(id);
        }
      }
      return children;
    },
    canAddChild() {
      return !!CHILD_TYPES_FOR_PARENT[this.item?.requirement_type_name];
    },
    canChangeParent() {
      return !!VALID_PARENT_TYPES[this.item?.requirement_type_name];
    },
    parentOptions() {
      if (!this.item) return [];
      const descendants = getDescendantIds([this.item.id], this.allItems);
      const options = this.allItems.filter((row) => {
        if (descendants.has(row.id)) return false;
        return canParent(row.requirement_type_name, this.item.requirement_type_name);
      });
      // The current parent may be outside the opening list (done, other
      // tenant); include it so the picker shows it instead of "None".
      if (this.parentItem && !options.some((row) => row.id === this.parentItem.id)) {
        options.unshift(this.parentItem);
      }
      return options;
    },
    allowedChildTypes() {
      const allowed = CHILD_TYPES_FOR_PARENT[this.item?.requirement_type_name];
      if (!allowed) return this.requirementTypes.filter((t) => WORK_TYPES.has(t.name));
      return this.requirementTypes.filter((t) => allowed.includes(t.name));
    },
    descendantCount() {
      if (!this.item) return 0;
      return getDescendantIds([this.item.id], this.allItems).size - 1;
    },
  },
  watch: {
    itemIdNumber(newId) {
      if (newId) this.load();
      else this.reset();
    },
    'item.description'(value) {
      if (!this.fieldSaveState.description) this.descriptionDraft = value || '';
    },
    'item.internal_notes'(value) {
      if (!this.fieldSaveState.internal_notes) this.internalNotesDraft = value || '';
    },
  },
  mounted() {
    if (this.itemIdNumber) this.load();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeCustomerLegend);
  },
  methods: {
    focusCommentComposer() {
      this.$refs.commentThread?.focusComposer();
    },
    _copyToClipboard(text, flagName) {
      if (!navigator.clipboard?.writeText) {
        this.toast.error('Could not copy to clipboard.');
        return;
      }
      return navigator.clipboard.writeText(text).then(() => {
        this[flagName] = true;
        setTimeout(() => { this[flagName] = false; }, 1200);
      }).catch(() => {
        this.toast.error('Could not copy to clipboard.');
      });
    },
    copyTicketId() {
      return this._copyToClipboard(String(this.item.id), 'idCopied');
    },
    copyReplyAddress() {
      return this._copyToClipboard(this.item.reply_address, 'replyAddressCopied');
    },
    toggleCustomerLegend() {
      this.showCustomerLegend = !this.showCustomerLegend;
      if (this.showCustomerLegend) {
        this.$nextTick(() => document.addEventListener('click', this.closeCustomerLegend));
      } else {
        document.removeEventListener('click', this.closeCustomerLegend);
      }
    },
    closeCustomerLegend() {
      this.showCustomerLegend = false;
      document.removeEventListener('click', this.closeCustomerLegend);
    },
    autoResize(el) {
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    autoResizeAll() {
      this.$nextTick(() => {
        this.autoResize(this.$refs.descriptionTextarea);
        this.autoResize(this.$refs.internalNotesTextarea);
      });
    },
    badgeClass(state) {
      return state === 'saved' ? 'text-emerald-600' : 'text-gray-400';
    },
    saveIcon(state) {
      return state === 'saved' ? 'check' : 'spinner';
    },
    saveText(state) {
      return state === 'saved' ? 'Saved' : 'Saving...';
    },
    setFieldState(field, state, forId) {
      if (forId != null && this.item?.id !== forId) return;
      this.fieldSaveState = { ...this.fieldSaveState, [field]: state };
    },
    // Optimistic edit through the store with an undo window. The PATCH goes
    // out when the toast settles; the response merges back into the row.
    writeField({ field, payload, local, endpoint, message, immediate = false }) {
      if (!this.isStaffView || !this.item?.id) return;
      const id = this.item.id;
      const prev = {};
      for (const key of Object.keys(local)) prev[key] = this.item[key];
      this.$store.dispatch('supportItems/patchLocal', { id, fields: local });

      const commit = async () => {
        this.setFieldState(field, 'saving', id);
        try {
          await this.$store.dispatch('supportItems/commit', { id, payload, endpoint, localKeys: Object.keys(local) });
          if (field === 'status' || field === 'parent') this.loadHistory();
          this.setFieldState(field, 'saved', id);
          setTimeout(() => {
            if (this.fieldSaveState[field] === 'saved') this.setFieldState(field, null, id);
          }, 2000);
        } catch (e) {
          this.setFieldState(field, null, id);
          throw e;
        }
      };
      const revert = () => {
        this.$store.dispatch('supportItems/revert', { id, fields: prev });
        this.setFieldState(field, null, id);
      };

      if (immediate) {
        commit().catch(() => {
          revert();
          this.toast.error(`Couldn't save the ${FIELD_LABELS[field] || field}. Your change was undone.`);
        });
        return;
      }
      undoQueue.show(message || `${FIELD_LABELS[field] || field} updated`, commit, revert);
    },
    startEditTitle() {
      this.titleDraft = this.item.title;
      this.editingTitle = true;
      this.$nextTick(() => this.$refs.titleInput?.focus());
    },
    cancelTitle() {
      this.editingTitle = false;
    },
    saveTitle() {
      if (!this.editingTitle) return;
      this.editingTitle = false;
      const next = this.titleDraft.trim();
      if (!next || next === this.item.title) return;
      this.writeField({ field: 'title', payload: { title: next }, local: { title: next }, message: 'Title updated' });
    },
    saveDescription() {
      const next = this.descriptionDraft;
      if ((next || '') === (this.item.description || '')) return;
      this.writeField({ field: 'description', payload: { description: next }, local: { description: next }, immediate: true });
    },
    saveInternalNotes() {
      const next = this.internalNotesDraft;
      if ((next || '') === (this.item.internal_notes || '')) return;
      this.writeField({ field: 'internal_notes', payload: { internal_notes: next }, local: { internal_notes: next }, immediate: true });
    },
    setTags(newTags) {
      this.writeField({ field: 'tags', payload: { tags: newTags }, local: { tags: newTags }, immediate: true });
    },
    setDueDate(value) {
      const next = value || null;
      if (next === (this.item.due_date || null)) return;
      this.writeField({
        field: 'due_date',
        payload: { due_date: next },
        local: { due_date: next },
        message: next ? `Due date set to ${formatDateValue(next)}` : 'Due date cleared',
      });
    },
    setPriority(value) {
      if (value === this.priorityValue) return;
      const code = value == null ? 'none' : PRIORITY_INT_TO_CODE[value];
      this.writeField({
        field: 'priority',
        payload: { priority: code },
        local: { priority: code },
        message: value != null ? `Priority set to P${value}` : 'Priority cleared',
      });
    },
    setStatus(status) {
      if (!status || status === this.item.status) return;
      this.writeField({
        field: 'status',
        payload: { status },
        local: { status },
        endpoint: 'set-status',
        message: `Status set to ${STATUS_LABELS[status] || status}`,
      });
    },
    setOwner(ownerId) {
      if ((ownerId ?? null) === (this.item.owner ?? null)) return;
      const user = this.users.find((u) => u.id === ownerId);
      const name = user ? user.full_name || user.email : null;
      this.writeField({
        field: 'owner',
        payload: { owner: ownerId },
        local: { owner: ownerId, owner_name: name },
        message: name ? `Assigned to ${name}` : 'Owner cleared',
      });
    },
    setTenant(value) {
      const tenantId = value === '' ? null : Number(value);
      if ((tenantId ?? null) === (this.item.tenant ?? null)) return;
      const tenant = this.tenantOptions.find((t) => t.id === tenantId);
      this.writeField({
        field: 'tenant',
        payload: { tenant: tenantId },
        local: { tenant: tenantId, tenant_name: tenant?.name || null },
        message: tenant ? `Moved to ${tenant.name}` : 'Tenant cleared',
      });
    },
    setType(typeId) {
      if (typeId === this.item.requirement_type) return;
      const type = this.requirementTypes.find((t) => t.id === typeId);
      if (!type) return;
      this.writeField({
        field: 'requirement_type',
        payload: { requirement_type: typeId },
        local: { requirement_type: typeId, requirement_type_name: type.name },
        message: `Type set to ${type.name}`,
      });
    },
    setParent(parentId) {
      const next = parentId ?? null;
      if (next === (this.item.parent ?? null)) return;
      const parent = next ? this.itemsById[next] : null;
      this.writeField({
        field: 'parent',
        payload: { parent: next },
        local: { parent: next },
        message: parent ? `Moved under ${parent.title}` : 'Moved to top level',
      });
    },
    toggleCustomerFacing() {
      const next = this.item.customer_facing === false;
      this.writeField({
        field: 'customer_facing',
        payload: { customer_facing: next },
        local: { customer_facing: next },
        message: next ? 'Now visible to the customer' : 'Now internal only',
      });
    },
    startAddChild() {
      this.addingChild = true;
      this.newChildTitle = '';
      this.newChildTypeId = this.allowedChildTypes[0]?.id || null;
      this.$nextTick(() => this.$refs.childTitleInput?.focus());
    },
    async submitChild() {
      if (!this.newChildTitle.trim() || !this.newChildTypeId) return;
      try {
        const data = await createTicket({
          title: this.newChildTitle.trim(),
          parent: this.item.id,
          tenant: this.item.tenant,
          requirement_type: this.newChildTypeId,
          status: 'open',
          priority: this.item.priority || 'none',
          customer_facing: false,
        });
        this.newChildTitle = '';
        this.$store.dispatch('supportItems/addToScope', { scope: this.scope, row: data });
      } catch {
        this.toast.error('Failed to create child. Please try again.');
      }
    },
    async deleteAndPromoteChildren() {
      const newParent = this.item.parent || null;
      const failed = [];
      for (const child of this.childItems) {
        try {
          await this.$store.dispatch('supportItems/update', { id: child.id, payload: { parent: newParent } });
        } catch {
          failed.push(child.title);
        }
      }
      if (failed.length) {
        this.toast.error(`Could not move ${failed.length} ${failed.length === 1 ? 'child' : 'children'}. Delete aborted.`);
        return;
      }
      await this.deleteWithChildren('Deleted. Children moved to parent.');
    },
    async deleteWithChildren(successMessage = 'Deleted successfully.') {
      const id = this.item.id;
      try {
        await deleteTicket(id);
        this.showDeleteConfirm = false;
        this.toast.success(successMessage);
        this.$emit('deleted', id);
        this.$store.dispatch('supportItems/remove', id);
      } catch {
        this.toast.error('Failed to delete. Please try again.');
      }
    },
    async load() {
      const id = this.itemIdNumber;
      this.reset();
      this.loading = true;
      this.loadFailed = false;
      try {
        await this.$store.dispatch('supportItems/fetchOne', id);
      } catch (err) {
        if (this.itemIdNumber !== id) return;
        if (!this.item) {
          this.loadFailed = true;
          this.toast.error(err?.response?.status === 404 ? 'Item not found.' : 'Could not load that item.');
        }
      } finally {
        if (this.itemIdNumber === id) this.loading = false;
      }
      if (this.itemIdNumber !== id || !this.item) return;
      this.descriptionDraft = this.item.description || '';
      this.internalNotesDraft = this.item.internal_notes || '';
      this.autoResizeAll();
      await Promise.allSettled([
        this.isStaffView ? this.loadHistory() : Promise.resolve(),
        this.isStaffView ? this.loadHierarchy() : Promise.resolve(),
        this.loadRequirementMedia(),
        this.loadFirstEmailSender(),
      ]);
    },
    // Children come from ?parent=<id> and ancestors from the detail endpoint,
    // rather than from allItems: the opening list may be open_only or
    // tenant_scoped, which drops done or other-tenant rows.
    async loadHierarchy() {
      const id = this.itemIdNumber;
      await Promise.allSettled([this.loadChildren(id), this.loadAncestors(id)]);
    },
    async loadChildren(id) {
      if (!CHILD_TYPES_FOR_PARENT[this.item?.requirement_type_name]) return;
      const data = await listTickets({ view: 'list', parent: id });
      if (this.itemIdNumber !== id || !Array.isArray(data)) return;
      for (const row of data) this.$store.dispatch('supportItems/upsert', row);
      this.fetchedChildIds = data.map((row) => row.id);
    },
    async loadAncestors(id) {
      const ids = [];
      const seen = new Set([id]);
      let parentId = this.item?.parent;
      while (parentId && !seen.has(parentId) && ids.length < MAX_ANCESTOR_DEPTH) {
        seen.add(parentId);
        let parent = this.allItems.find((row) => row.id === parentId);
        if (!parent) {
          parent = await this.$store.dispatch('supportItems/fetchOne', parentId);
          if (this.itemIdNumber !== id) return;
          ids.push(parentId);
          this.fetchedAncestorIds = [...ids];
        }
        parentId = parent?.parent;
      }
    },
    reset() {
      this.fetchedChildIds = [];
      this.fetchedAncestorIds = [];
      this.activities = [];
      this.activitiesError = false;
      this.requirementMediaFiles = [];
      this.firstEmailSenderValue = null;
      this.fieldSaveState = {};
      this.editingTitle = false;
      this.addingChild = false;
      this.showDeleteConfirm = false;
      this.descriptionDraft = '';
      this.internalNotesDraft = '';
    },
    async loadRequirementMedia() {
      const id = this.item?.id;
      if (!id) return;
      try {
        const data = await listAttachments(id);
        if (this.item?.id !== id) return;
        const rows = Array.isArray(data) ? data : data.results || [];
        // Comment-scoped files render inside their comment, not twice.
        this.requirementMediaFiles = rows.filter((f) => !f.comment);
      } catch {
        if (this.item?.id === id) this.requirementMediaFiles = [];
      }
    },
    async removeAttachment(file) {
      // Native dialogs sit outside the Demo Mode mask; file names can name people.
      const name = isDemoMode() ? 'this attachment' : (file.filename || file.file_name || 'this attachment');
      if (!window.confirm(`Remove ${name} from this ticket? This can't be undone.`)) return;
      try {
        await deleteAttachment(file.id);
        await this.loadRequirementMedia();
        this.toast.success('Attachment removed.');
      } catch {
        this.toast.error('Could not remove that attachment.');
      }
    },
    async onAttachmentUploaded() {
      await this.loadRequirementMedia();
      const uploader = this.$refs.attachUpload;
      if (uploader && !uploader.hasPending()) uploader.clear();
    },
    async loadFirstEmailSender() {
      if (!this.item?.id || !this.item?.needs_triage) return;
      try {
        const data = await listComments(this.item.id);
        const comments = Array.isArray(data) ? data : data.results || [];
        const emailComment = comments.find((c) => c.email_sender);
        if (emailComment) this.firstEmailSenderValue = emailComment.email_sender;
      } catch {
        // ignore
      }
    },
    onNewCommentCount(count) {
      this.$store.dispatch('supportItems/patchLocal', { id: this.item.id, fields: { comment_count: count } });
      this.$store.dispatch('supportItems/revert', { id: this.item.id, fields: {} });
      this.$emit('comment-count', { id: this.item.id, count });
    },
    onCommentPosted() {
      this.$store.dispatch('supportItems/fetchOne', this.item.id).catch(() => {});
    },
    onNewComments() {
      if (this.isStaffView) this.loadHistory();
      this.$store.dispatch('supportItems/fetchOne', this.item.id).catch(() => {});
    },
    onTriaged(data) {
      this.$store.dispatch('supportItems/upsert', {
        id: this.item.id,
        needs_triage: false,
        tenant: data.tenantId,
        tenant_name: data.tenantName,
        requester: data.requesterId,
        requester_name: data.requesterName,
      });
      this.$emit('triaged', { id: this.item.id, ...data });
    },
    async loadHistory() {
      const id = this.item?.id;
      if (!id) return;
      try {
        const data = await getTicketHistory(id);
        if (this.item?.id !== id) return;
        this.activities = data;
        this.activitiesError = false;
      } catch {
        if (this.item?.id === id) {
          this.activities = [];
          this.activitiesError = true;
        }
      }
    },
    activityCommentPreview(a) {
      if (a.activity_type !== 'commented') return '';
      return a.metadata?.body || a.description || '';
    },
    activityIcon(type) {
      const icons = {
        created: ['fas', 'plus'],
        updated: ['fas', 'pencil'],
        status_changed: ['fas', 'arrow-right'],
        assigned: ['fas', 'user'],
        completed: ['fas', 'check'],
        reopened: ['fas', 'rotate-left'],
        linked: ['fas', 'link'],
        unlinked: ['fas', 'link-slash'],
        reordered: ['fas', 'arrows-up-down'],
        commented: ['fas', 'comment'],
      };
      return icons[type] || ['fas', 'circle'];
    },
    formatActivity(a) {
      const name = `<span class="font-medium text-gray-900">${this.escapeHtml(a.user_name || 'System')}</span>`;
      const changes = a.field_changes || [];
      const value = (c, key) => c[key] ?? c[key === 'old_value' ? 'from' : 'to'];
      switch (a.activity_type) {
        case 'created':
          return `${name} created this`;
        case 'completed': {
          const sc = changes.find((c) => c.field === 'status');
          const suffix = a.description ? ` — ${this.escapeHtml(a.description)}` : '';
          return sc
            ? `${name} marked this ${this.escapeHtml(STATUS_LABELS[value(sc, 'new_value')] || value(sc, 'new_value'))}${suffix}`
            : `${name} marked this complete${suffix}`;
        }
        case 'reopened':
          return `${name} reopened this${a.description ? ` — ${this.escapeHtml(a.description)}` : ''}`;
        case 'reordered':
          return `${name} reordered this`;
        case 'linked':
          return `${name} linked to ${this.escapeHtml(a.metadata?.target_title || 'item')} (${this.escapeHtml(a.metadata?.link_type || 'related')})`;
        case 'unlinked':
          return `${name} removed link to ${this.escapeHtml(a.metadata?.target_title || 'item')}`;
        case 'assigned': {
          const assignee = changes.find((c) => c.field === 'owner');
          return assignee
            ? `${name} assigned to ${this.escapeHtml(value(assignee, 'new_value') || 'Unassigned')}`
            : `${name} changed assignment`;
        }
        case 'status_changed': {
          const sc = changes.find((c) => c.field === 'status');
          const from = sc ? this.escapeHtml(value(sc, 'old_value')) : '';
          const to = sc ? this.escapeHtml(value(sc, 'new_value')) : '';
          if (a.description) {
            const description = this.escapeHtml(a.description);
            return sc ? `${name} — ${description} (${from} -> ${to})` : `${name} — ${description}`;
          }
          return sc ? `${name} changed status from ${from} to ${to}` : `${name} changed status`;
        }
        case 'updated': {
          if (!changes.length) return `${name} updated this`;
          return changes
            .map((c) => `${name} changed ${this.escapeHtml(c.field)} from ${this.escapeHtml(value(c, 'old_value') || 'empty')} to ${this.escapeHtml(value(c, 'new_value') || 'empty')}`)
            .join('<br>');
        }
        case 'commented': {
          const src = a.metadata?.source;
          const badge = src === 'internal_note'
            ? ' <span class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 ml-1">Internal</span>'
            : '';
          return `${name} commented${badge}`;
        }
        default:
          return `${name} ${this.escapeHtml(a.activity_type_display || a.activity_type)}`;
      }
    },
    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    typeClass(type) {
      return TYPE_CLASSES[type] || 'bg-gray-100 text-gray-600';
    },
    typeLabel(type) {
      if (this.isCustomerView) return customerTypeLabel(type);
      return type;
    },
    statusClass(status) {
      if (this.isCustomerView) return customerStatusClass(status);
      return STATUS_CLASSES[status] || 'bg-gray-100 text-gray-500';
    },
    statusLabel(status) {
      if (this.isCustomerView) return customerStatusLabel(status);
      return STATUS_LABELS[status] || status;
    },
    statusTitle(status) {
      if (this.isCustomerView) return customerStatusHint(status);
      return STATUS_LABELS[status] || status;
    },
    statusAriaLabel(status) {
      const label = this.statusLabel(status);
      const hint = this.statusTitle(status);
      return !this.isCustomerView || !hint || hint === label ? label : `${label}. ${hint}`;
    },
    formatDate(dateStr) {
      return formatDateValue(dateStr);
    },
    relativeTime(dateStr) {
      if (!dateStr) return '';
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return this.formatDate(dateStr);
    },
    dueDateClass(date) {
      const diffDays = daysUntil(date);
      if (diffDays == null) return 'text-gray-400';
      if (diffDays < 0) return 'text-red-600 font-medium';
      if (diffDays <= 3) return 'text-amber-600';
      return 'text-gray-600';
    },
  },
};
</script>
